import { Suspense, lazy, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/auth/AuthContext";
import { AuthCallback } from "@/auth/AuthCallback";
import { AiProvider } from "@/ai/AiContext";
import { AiDrawer } from "@/ai/AiDrawer";
import Landing from "@/pages/Landing";

const lazyPage = (loader) => lazy(loader);
const Members = lazyPage(() => import("@/pages/Members"));
const Admin = lazyPage(() => import("@/pages/Admin"));
const Markets = lazyPage(() => import("@/pages/Markets"));
const Tools = lazyPage(() => import("@/pages/Tools"));
const Observer = lazyPage(() => import("@/pages/Observer"));
const Research = lazyPage(() => import("@/pages/Research"));
const Calendar = lazyPage(() => import("@/pages/Calendar"));
const Scanners = lazyPage(() => import("@/pages/Scanners"));
const Options = lazyPage(() => import("@/pages/Options"));
const Mentorship = lazyPage(() => import("@/pages/Mentorship"));
const ThankYou = lazyPage(() => import("@/pages/ThankYou"));
const Learn = lazyPage(() => import("@/pages/Learn"));
const Article = lazyPage(() => import("@/pages/Article"));
const Legal = lazyPage(() => import("@/pages/Legal"));

function PageFallback() {
  return <div className="auth-callback" data-testid="page-loading"><span className="pulse-dot" /> Loading…</div>;
}
import { LangProvider } from "@/tools/i18n";
import "@/App.css";

function useLenis() {
  const lenis = useRef(null);
  useEffect(() => {
    const fine = window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 801px)").matches;
    if (!fine) return undefined;
    const l = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenis.current = l;
    let raf;
    const loop = (t) => { l.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); l.destroy(); lenis.current = null; };
  }, []);
  return (id, immediate = false) => {
    const l = lenis.current;
    const el = document.getElementById(id);
    if (!l) { if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 70, behavior: immediate ? "auto" : "smooth" }); return; }
    l.resize(); l.scrollTo(`#${id}`, { offset: -70, duration: 1.4, force: true, immediate });
  };
}

function AppRouter() {
  const location = useLocation();
  const scrollTo = useLenis();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return <Suspense fallback={<PageFallback />}><Routes>
    <Route path="/" element={<Landing scrollTo={scrollTo} />} />
    <Route path="/members" element={<Members />} />
    <Route path="/admin" element={<Admin />} />
    <Route path="/markets" element={<Markets scrollTo={scrollTo} />} />
    <Route path="/tools" element={<Tools scrollTo={scrollTo} />} />
    <Route path="/tools/:tool" element={<Tools scrollTo={scrollTo} />} />
    <Route path="/observer" element={<Observer scrollTo={scrollTo} />} />
    <Route path="/research" element={<Research scrollTo={scrollTo} />} />
    <Route path="/research/:symbol" element={<Research scrollTo={scrollTo} />} />
    <Route path="/calendar" element={<Calendar scrollTo={scrollTo} />} />
    <Route path="/scanners" element={<Scanners scrollTo={scrollTo} />} />
    <Route path="/options" element={<Options scrollTo={scrollTo} />} />
    <Route path="/options/:symbol" element={<Options scrollTo={scrollTo} />} />
    <Route path="/mentorship" element={<Mentorship scrollTo={scrollTo} />} />
    <Route path="/learn" element={<Learn scrollTo={scrollTo} />} />
    <Route path="/learn/category/:cat" element={<Learn scrollTo={scrollTo} />} />
    <Route path="/learn/:slug" element={<Article scrollTo={scrollTo} />} />
    <Route path="/disclaimer" element={<Legal kind="disclaimer" scrollTo={scrollTo} />} />
    <Route path="/privacy-policy" element={<Legal kind="privacy" scrollTo={scrollTo} />} />
    <Route path="/thank-you" element={<ThankYou />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes></Suspense>;
}

function App() {
  return <AuthProvider>
    <AiProvider>
      <LangProvider>
        <AppRouter />
        <AiDrawer />
        <Toaster position="bottom-center" theme="dark" toastOptions={{ style: { background: "#0b2518", border: "1px solid #1e4530", color: "#f3efe0" } }} />
      </LangProvider>
    </AiProvider>
  </AuthProvider>;
}

export default App;
